import { isUzError } from "@uzverse/core";
import type { ErrorCode, TaskState } from "@uzverse/core";
import { useTranslation } from "react-i18next";
import { TASK_PROGRESS } from "../state/store.js";

/** A translate function for the shared `common` namespace (`ui.*` strings of `@uzverse/ui`). */
export function useCommonT(): (key: string, vars?: Record<string, unknown>) => string {
  const { t } = useTranslation("common");
  return (key, vars) => String(t(key, vars));
}

/** Turns any thrown value into a localized, user-safe message. */
export function useErrorText(): (err: unknown) => string {
  const { t } = useTranslation();
  return (err) => {
    const code: ErrorCode =
      typeof err === "string" ? (err as ErrorCode) : isUzError(err) ? err.code : "internal";
    return t(`errors.${code}`, { ns: "common" });
  };
}

export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="note note-error">
      {message}
    </p>
  );
}

export function TaskChip({ state }: { state: TaskState }) {
  const { t } = useTranslation("web");
  const tone =
    state === "done"
      ? "ok"
      : state === "needs_owner" || state === "cancelled"
        ? "warn"
        : state === "awaiting_approval"
          ? "ask"
          : "busy";
  return (
    <span role="status" className={`chip chip-${tone}`}>
      {t(`task.state.${state}`)}
    </span>
  );
}

export function TaskProgress({ state, label }: { state: TaskState; label: string }) {
  const value = TASK_PROGRESS[state];
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      className="progress"
    >
      <div className="progress-bar" style={{ width: `${value}%` }} data-state={state} />
    </div>
  );
}
